import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";

export interface Project {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  icon?: string;
  owner_id: string;
  user_id: string;
  status: 'active' | 'completed' | 'archived';
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  growth_squad?: {
    id: string;
    name: string;
    alias?: string;
  };
}

export interface CreateProjectParams {
  name: string;
  description?: string;
  icon?: string;
  owner_id: string;
  status?: 'active' | 'completed' | 'archived';
  start_date?: string;
  end_date?: string;
}

export interface UpdateProjectParams {
  name?: string;
  description?: string;
  icon?: string;
  owner_id?: string;
  status?: 'active' | 'completed' | 'archived';
  start_date?: string;
  end_date?: string;
}

export const useProjects = (brandId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();

  const targetBrandId = brandId || currentBrandId;

  const { data: projects = [], isLoading: isQueryLoading } = useQuery({
    queryKey: ['projects', targetBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_tasks_projects')
        .select(`
          *,
          growth_squad!owner_id(id, name, alias)
        `)
        .eq('brand_id', targetBrandId!)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as Project[];
    },
    enabled: !!user && !!targetBrandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const refetchProjects = () => {
    queryClient.invalidateQueries({ queryKey: ['projects', targetBrandId] });
  };

  const createProject = async (params: CreateProjectParams): Promise<Project | null> => {
    if (!user || !targetBrandId) {
      toast({
        title: "Error",
        description: "Missing user or brand ID",
        variant: "destructive",
      });
      return null;
    }

    try {
      setIsLoading(true);
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive",
        });
        return null;
      }

      const { data, error } = await supabase
        .from('os_tasks_projects')
        .insert({
          ...params,
          brand_id: targetBrandId,
          user_id: user.user.id,
          status: params.status || 'active',
          icon: params.icon || 'Folder',
        })
        .select(`
          *,
          growth_squad!owner_id(id, name, alias)
        `)
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project created successfully",
      });

      refetchProjects();
      return data as Project;
    } catch (error: any) {
      console.error('Error creating project:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create project",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProject = async (projectId: string, params: UpdateProjectParams): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('os_tasks_projects')
        .update({
          ...params,
          updated_at: new Date().toISOString(),
        })
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project updated successfully",
      });

      refetchProjects();
      return true;
    } catch (error: any) {
      console.error('Error updating project:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update project",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProject = async (projectId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('os_tasks_projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });

      refetchProjects();
      return true;
    } catch (error: any) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete project",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    projects,
    isLoading: isQueryLoading || isLoading,
    createProject,
    updateProject,
    deleteProject,
    refetch: refetchProjects,
  };
};
